import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "react-datepicker/dist/react-datepicker.css";
import "bootstrap-daterangepicker/daterangepicker.css";
import DefaultProductImg from "img/product/default.png";
import Collapse from "react-bootstrap/Collapse";
import { Button } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { reportHandler } from "../../redux/handlers";
import { ReceiptPrintPage } from "components/BillPaper";
import ReportBillDetailPrintPopup from "./ReportBillDetailPrintPopup";

const formatNumber = (value) => {
  if (value === undefined || value === null || value === "-") return "-";
  const num = typeof value === "string" ? parseFloat(value) : value;
  return num.toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const BILL_STATUS = {
  OPEN: 1,
  PENDING: 2,
  COMPLETED: 3,
  CANCELED: 4,
  RETURNED: 5,
};

const DELIVERY_OPTIONS_MAP = new Map([
  ["DINE_IN", 1],
  ["TAKEAWAY", 2],
  ["DELIVERY", 3],
]);

const redStrikethrough = {
  textDecoration: "line-through",
  textDecorationColor: "red",
  textDecorationThickness: "1px",
};

const noPointerStyle = {
  pointerEvents: "none",
  cursor: "default",
};

export function ReportBillDetail() {
  let { businessId, billID } = useParams();
  const navigate = useNavigate();
  const [collapseShow, setCollapseShow] = useState(false);
  const [orderBill, setOrderBill] = useState(null);
  const dispatch = useDispatch();
  const [billdata, setBilldata] = useState(null);
  const [memberDetails, setMemberDetails] = useState(null);
  const [currentPOS, setCurrentPOS] = useState([]);
  const billRef = useRef();

  const [isPrintPopupOpen, setIsPrintPopupOpen] = useState(false);
  const [isPrintCopy, setIsPrintCopy] = useState(false);

  const handlePrint = () => {
    setIsPrintCopy(true);
    setIsPrintPopupOpen(true);
  };

  const handlePrintComplete = () => {
    setIsPrintPopupOpen(false);
  };

  const formatData = (rawData) => {
    try {
      let billStatus = BILL_STATUS.COMPLETED;
      if (rawData.status === "OPEN") billStatus = BILL_STATUS.OPEN;
      else if (rawData.status === "PENDING") billStatus = BILL_STATUS.PENDING;
      else if (rawData.status === "COMPLETED")
        billStatus = BILL_STATUS.COMPLETED;
      else if (rawData.status === "CANCELED") billStatus = BILL_STATUS.CANCELED;
      else if (rawData.status === "RETURNED") billStatus = BILL_STATUS.RETURNED;

      const delivery = DELIVERY_OPTIONS_MAP.get(rawData.order.order_type) || 1;

      const updateData = {
        billID: rawData.id,
        order_id: rawData.order_id,
        receiptNo: rawData.receipt_no || "-",
        refund_no: rawData.refund_no || null,
        pos: rawData.order.branchPosSelling.branchPos.name,
        billStatus: billStatus,
        staff: `${rawData.userBusinessGroup.first_name} ${rawData.userBusinessGroup.last_name}`,
        customerID: rawData.member_id,
        customerName: "",
        orderNo: rawData.order.code,
        orderDate: new Date(rawData.created_at).toLocaleString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }),
        billOpenTime: new Date(rawData.order.created_at).toLocaleTimeString(
          "en-US",
          {
            hour12: true,
            hour: "numeric",
            minute: "2-digit",
          }
        ),
        billPaymentTime:
          rawData.payment && rawData.payment.length > 0
            ? new Date(rawData.payment[0].created_at).toLocaleTimeString(
                "en-US",
                {
                  hour12: true,
                  hour: "numeric",
                  minute: "2-digit",
                }
              )
            : "-",
        tableID: "",
        tableName: "",
        zone: "",
        delivery: delivery,
        taxInvoice: null,
        billReturned: rawData.billReturned || [],
        created_at: rawData.created_at,
        order: rawData.order,
        product: rawData.billItems.map((billItem) => ({
          id: billItem.orderItem.id.toString(),
          billItemId: billItem.id,
          productID: billItem.orderItem.product_id,
          productImage:
            billItem.orderItem.product.image_path || DefaultProductImg,
          productName:
            billItem.orderItem.product.name || billItem.orderItem.item_name,
          productPrice: billItem.orderItem.price,
          productPricePerPiece: billItem.orderItem?.product?.price || 0,
          productSumPrice: billItem.price,
          productQty: billItem.quantity,
          productStatus:
            billItem.orderItem?.status_kitchen === "ORDER_CANCEL" ? 4 : 0,
          optionnote: billItem.orderItem.orderProductAnnotation
            ? billItem.orderItem.orderProductAnnotation.map((annotation) => ({
                listID: annotation.id.toString(),
                itemName: annotation.productAnnotationItem?.name || "",
              }))
            : [],
          optionproduct: billItem.orderItem.orderProductOption
            ? billItem.orderItem.orderProductOption.map((option) => ({
                listID: option.id.toString(),
                itemName: option.productOptionItem?.name || "",
                price: option.productOptionItem?.price || 0,
                qty: option.qty || 1,
              }))
            : [],
          optionstandard: billItem.orderItem.orderProductSpecificOption
            ? billItem.orderItem.orderProductSpecificOption.map((option) => ({
                listID: option.id.toString(),
                itemName: option.productSpecificOptionItem?.name || "",
                price: option.productSpecificOptionItem?.price || 0,
                qty: option.qty || 1,
              }))
            : [],
          note: billItem.orderItem.remark || "",
        })),
        orderPrice: {
          final_profit_loss_rounded: rawData.final_profit_loss_rounded,
          taxable_amount: rawData.taxable_amount,
          sumprice:
            billStatus === 3 || billStatus === 5
              ? rawData.final_profit_loss_rounded
              : rawData.total_price,
          discount: rawData.discount_amount,
          vat: rawData.vat,
          net: Number(rawData.final_amount) || Number(rawData.total_price),
        },
        payment: rawData.payment,
        total_price: rawData.total_price,
        total_before_discount: rawData.total_before_discount,
        discount_amount: rawData.discount_amount,
        discount_points: rawData.discount_points || "0",
        total_after_discount: rawData.total_after_discount,
        service_charge: rawData.service_charge || "0",
        service_charge_percent: rawData.service_charge_percent || "0",
        taxable_amount: rawData.taxable_amount,
        vat: rawData.vat,
        grand_total: rawData.grand_total,
        credit_card_fee: rawData.credit_card_fee || "0",
        vat_on_fee: rawData.vat_on_fee || "0",
        final_amount: rawData.final_amount,
        final_payment_amount: rawData.final_payment_amount,
        additional_data: rawData.additional_data || {
          point: {
            spend: 0,
            earned: 0,
            remain: 0,
            total: 0,
          },
        },
        member: rawData.member || null,
      };
      return updateData;
    } catch (error) {
      console.error("Error formatting data:", error);
      return null;
    }
  };

  useEffect(() => {
    const prepareMemberDetails = () => {
      let memberData = {
        customerName: "-",
        customerPhone: "-",
        point: {
          all: "-",
          receive: "-",
          usage: "-",
          remain: "-",
          total: "-",
        },
        discount: {
          endBill: "0.00",
          promotion: "0.00",
          point: "0.00",
        },
        payment: {
          items: [],
        },
      };

      if (billdata?.member) {
        const billMember = billdata.member;
        const additionalDataPoints = billdata.additional_data?.point || {};

        memberData = {
          ...memberData,
          customerName: `${billMember.first_name_th || ""} ${
            billMember.last_name_th || ""
          }`.trim(),
          customerPhone: billMember.phone_number,
          point: {
            all: additionalDataPoints.total || 0,
            receive: additionalDataPoints.earned || 0,
            usage: additionalDataPoints.spend || 0,
            remain: additionalDataPoints.remain || 0,
            total: additionalDataPoints.remain || 0,
          },
        };
      }

      if (billdata && billdata.billStatus === 3) {
        memberData = {
          ...memberData,
          discount: {
            endBill:
              billdata.orderPrice?.discount ||
              billdata.discount_amount ||
              "0.00",
            promotion: "0.00",
            point: billdata.discount_points || "0.00",
          },
          payment: {
            items:
              billdata.payment?.map((item) => ({
                name: item.paymentMethod?.name || "เงินสด",
                amount:
                  item.price_after_cash_out != 0
                    ? item.price_after_cash_out
                    : item.price || "0.00",
              })) || [],
          },
        };
      }
      setMemberDetails(memberData);
    };

    if (billdata) {
      prepareMemberDetails();
    }
  }, [billdata]);

  useEffect(() => {
    async function fetchBill() {
      try {
        const data = await dispatch(reportHandler.getBillReportById(billID));
        const formattedData = formatData(data);
        setBilldata(formattedData);
        setOrderBill(data);
      } catch (error) {
        console.error("Error fetching bill:", error);
      }
    }
    fetchBill();
  }, [billID, dispatch]);

  const getDisplayNumber = () => {
    if (billdata?.billStatus === 5 && billdata.refund_no) {
      return billdata.refund_no;
    } else {
      return billdata?.orderNo;
    }
  };

  const getNumberLabel = () => {
    if (billdata?.billStatus === 5) {
      return "Refund No.";
    } else {
      return "Order No.";
    }
  };

  const CustomerInfoSection = () => (
    <div className="pe-3">
      <div className="fs-18 fw-500 text-[#2DC76D] mb-3">ข้อมูลสมาชิก</div>

      <div className="d-flex align-items-center mb-3">
        <img
          className="aspect-square w-[min(6.13vw,62px)] rounded-full object-cover"
          src="/images/avatar-female.png"
          alt=""
        />
        <div className="ms-3 fs-14 text-[#2F4858]">
          <div>{memberDetails?.customerName || "-"}</div>
          <div>{memberDetails?.customerPhone || "-"}</div>
        </div>
      </div>

      <div className="d-flex align-items-center mb-1">
        <div className="me-auto text-[#2F4858] fs-14 fw-light">
          แต้มสะสมที่มีอยู่
        </div>
        <div className="text-[#2F4858]">
          {formatNumber(memberDetails?.point?.remain)}
        </div>
      </div>
      <div className="d-flex align-items-center mb-1">
        <div className="me-auto text-[#2F4858] fs-14 fw-light">
          คะแนนสะสมที่ใช้
        </div>
        <div className="text-[#2F4858]">
          {formatNumber(memberDetails?.point?.usage)}
        </div>
      </div>
      <div className="d-flex align-items-center mb-1">
        <div className="me-auto text-[#2F4858] fs-14 fw-light">
          ได้รับคะแนนสะสม
        </div>
        <div className="text-[#2F4858]">
          {formatNumber(memberDetails?.point?.receive)}
        </div>
      </div>
      <div className="d-flex align-items-center mb-1">
        <div className="me-auto text-[#2F4858] fs-14 fw-light">
          คะแนนสะสมทั้งหมด
        </div>
        <div className="text-[#2F4858]">
          {formatNumber(memberDetails?.point?.all)}
        </div>
      </div>

      <hr />

      <div className="fs-18 fw-500 text-[#2DC76D] mb-2 mt-2">
        ส่วนลดและโปรโมชั่นที่ได้รับ
      </div>
      <div className="d-flex align-items-center mb-1">
        <div className="me-auto text-[#2F4858] fs-14 fw-light">
          ส่วนลดท้ายบิล
        </div>
        <div className="text-[#2F4858]">
          {formatNumber(
            billdata && billdata.billStatus === 3
              ? parseFloat(billdata.orderPrice?.discount || 0)
              : billdata && billdata.billStatus !== 3
              ? "0.00"
              : memberDetails?.discount?.endBill || "0.00"
          )}
        </div>
      </div>
      <div className="d-flex align-items-center mb-1">
        <div className="me-auto text-[#2F4858] fs-14 fw-light">
          ส่วนลดโปรโมชั่น
        </div>
        <div className="text-[#2F4858]">
          {formatNumber(
            billdata && billdata.billStatus === 3
              ? "0.00"
              : memberDetails?.discount?.promotion || "0.00"
          )}
        </div>
      </div>

      <hr />

      <div className="fs-18 fw-500 text-[#2DC76D] mb-2 mt-2">รายได้อื่นๆ</div>
      <div className="d-flex align-items-center mb-1">
        <div className="me-auto text-[#2F4858] fs-14 fw-light">
          Service Charge{" "}
          {billdata?.service_charge_percent
            ? `(${billdata.service_charge_percent}%)`
            : ""}
        </div>
        <div className="text-[#2F4858]">
          {formatNumber(
            billdata && billdata.billStatus === 3
              ? parseFloat(billdata.service_charge || 0)
              : "0.00"
          )}
        </div>
      </div>
      <div className="d-flex align-items-center mb-1">
        <div className="me-auto text-[#2F4858] fs-14 fw-light">
          ค่าธรรมเนียมบัตรเครดิต
        </div>
        <div className="text-[#2F4858]">
          {formatNumber(
            billdata && billdata.billStatus === 3
              ? parseFloat(billdata.credit_card_fee || 0)
              : "0.00"
          )}
        </div>
      </div>

      <hr />

      <div className="fs-18 fw-500 text-[#2DC76D] mb-2 mt-2">
        ช่องทางการชำระเงิน
      </div>

      {memberDetails?.payment?.items &&
      memberDetails.payment.items.length > 0 ? (
        memberDetails.payment.items.map((item, index) => (
          <div className="d-flex align-items-center mb-1" key={index}>
            <div className="me-auto text-[#565DFF] fs-14 fw-light">
              {item.name}
            </div>
            <div className="text-[#565DFF]">
              {formatNumber(parseFloat(item.amount))}
            </div>
          </div>
        ))
      ) : (
        <div className="d-flex align-items-center mb-1">
          <div className="me-auto text-[#565DFF] fs-14 fw-light">เงินสด</div>
          <div className="text-[#565DFF]">0.00</div>
        </div>
      )}
    </div>
  );

  const BillDetailsSection = () => (
    <div
      className="p-3 position-relative"
      style={{
        height: "calc(100vh - 175px)",
        borderRadius: "8px",
        background: "#FFF",
        boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.14)",
      }}
    >
      <div className="d-flex align-items-center">
        <div className="me-auto">
          <span className="text-14 text-[#2DC76D] me-2">
            {getNumberLabel()}
          </span>
          <span className="text-14 text-[#2F4858]">{getDisplayNumber()}</span>
        </div>
        {billdata?.billStatus === BILL_STATUS.OPEN && (
          <div className="bill-status-s2">เปิดบิลอยู่</div>
        )}
        {billdata?.billStatus === BILL_STATUS.PENDING && (
          <div className="bill-status-s1">พักบิล</div>
        )}
        {billdata?.billStatus === BILL_STATUS.COMPLETED && (
          <div className="bill-status-s3">สำเร็จ</div>
        )}
        {billdata?.billStatus === BILL_STATUS.CANCELED && (
          <div className="bill-status-s4">ยกเลิก</div>
        )}
        {billdata?.billStatus === BILL_STATUS.RETURNED && (
          <div className="bill-status-s4">คืนสินค้า</div>
        )}
      </div>

      <div>
        <span className="text-14 text-[#2DC76D] me-2">Receipt No.</span>
        <span className="text-[#2F4858] text-14">
          {billdata?.receiptNo || "-"}
        </span>
      </div>

      <div>
        <span className="text-12 text-[#2DC76D] me-2">พนักงาน :</span>
        <span className="text-12 text-[#2F4858]">{billdata?.staff || "-"}</span>
      </div>
      <div>
        <span className="text-12 text-[#2DC76D] me-2">วันที่ :</span>
        <span className="text-12 text-[#2F4858] me-2">
          {billdata?.orderDate}
        </span>
        <span className="text-12 text-[#2DC76D] me-2">จุดขาย :</span>
        <span className="text-12 text-[#2F4858]">{billdata?.pos || "-"}</span>
      </div>

      <div className="row g-0">
        <div className="col">
          <div>
            <span className="text-12 text-[#2DC76D] me-2">เวลาเปิดบิล :</span>
            <span className="text-12 text-[#2F4858] me-2">
              {billdata?.billOpenTime}
            </span>
            <span className="text-12 text-[#2DC76D] me-2">เวลาชำระเงิน :</span>
            <span className="text-12 text-[#2F4858]">
              {billdata?.billPaymentTime}
            </span>
          </div>
        </div>
        <div className="col-auto">
          <div className="text-14 text-[#2F4858] text-end mb-1">Total</div>
          <div className="text-16 fw-500 text-[#2DC76D]">
            {formatNumber(Number(billdata?.orderPrice?.sumprice || 0))}
          </div>
        </div>
      </div>

      <div className="my-2">
        {billdata?.delivery === 1 && <div className="dl1">ทานที่ร้าน</div>}
        {billdata?.delivery === 2 && <div className="dl2">กลับบ้าน</div>}
        {billdata?.delivery === 3 && <div className="dl3">เดลิเวอรี่</div>}
      </div>

      <div>
        <div className="">
          <div className="row gx-2 tc-17 fs-12">
            <div className="col-7">รายการ</div>
            <div className="col-2 text-center">จำนวน</div>
            <div className="col-3 text-end">ราคา</div>
          </div>
        </div>
        <hr className="mt-1 mb-0" />

        <div
          className="bx-main-order-list overflow-y-auto"
          style={{ height: "calc(100vh - 440px)" }}
        >
          <ul className="p-0 m-0 w-100">
            {billdata?.product &&
              billdata.product.map((item) => (
                <li className="px-2" key={item.id} style={noPointerStyle}>
                  <div className="row gx-2 align-items-center">
                    <div className="col-7 d-flex align-items-center text-black py-2">
                      <img
                        src={item.productImage}
                        className="foodimg max-w-[120px]"
                        alt={item.productName}
                        onError={(e) => (e.target.src = DefaultProductImg)}
                      />
                      <div
                        className="fs-12"
                        style={item.productStatus === 4 ? redStrikethrough : {}}
                      >
                        {item.productName}
                      </div>
                    </div>
                    <div className="col-2 fs-14 text-center foodQtyli">
                      {item.productQty}
                    </div>
                    <div className="col-3 fs-14 text-end">
                      <span
                        className="foodPriceli"
                        style={item.productStatus === 4 ? redStrikethrough : {}}
                      >
                        {formatNumber(item.productSumPrice)}
                      </span>
                    </div>
                  </div>
                  <div className="d-flex flex-wrap bx-Foption m-[1px]">
                    {item.optionstandard &&
                      item.optionstandard.map((option) => (
                        <div className="Foption mt-2" key={option.listID}>
                          {option.itemName}
                        </div>
                      ))}
                    {item.optionproduct &&
                      item.optionproduct.map((option) => (
                        <div className="Foption mt-2" key={option.listID}>
                          {option.itemName}
                        </div>
                      ))}
                  </div>

                  {((item.optionnote && item.optionnote.length > 0) ||
                    item.note) && (
                    <div className="fNote">
                      {item.optionnote &&
                        item.optionnote.map((note, index) => (
                          <span key={note.listID}>
                            {note.itemName}{" "}
                            {index !== item.optionnote.length - 1 ? "," : ""}{" "}
                          </span>
                        ))}
                      {item.note}
                    </div>
                  )}
                </li>
              ))}
          </ul>

          {billdata?.taxInvoice && (
            <div className="px-1 py-3">
              <div className="bg-[#FFF8F3] rounded-lg p-2 fs-14">
                <div className="text-[#FF6B00] mb-1">ออกใบกำกับเต็มรูปแบบ</div>
                <div className="text-[#061126]">
                  {billdata.taxInvoice.invoice_number}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {billdata?.billReturned && billdata.billReturned.length > 0 && (
        <div className="absolute p-3 bottom-[0%] w-100 left-[0%] -translate-y-20 justify-center">
          <div className="w-full bg-[#FACCCD26] rounded-xl p-2 flex flex-col justify-between">
            <div className="justify-between items-center flex">
              <p className="text-[#FF0000] text-md font-black">คืนสินค้า</p>
            </div>
            <div className="flex justify-between">
              <p className="text-[#0760FC] text-md font-medium">
                {billdata.billReturned[0].childBill?.receipt_no}
              </p>
              <p className="text-[#0760FC] text-md font-medium">
                {formatNumber(
                  billdata.billReturned[0].childBill?.total_price || "0"
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="absolute p-3 bottom-[0%] w-100 left-[0%]">
        <Collapse in={collapseShow}>
          <div id="collapse1">
            <div className="card card-body border-0 bg-c1 px-3 py-2">
              <div className="d-flex fs-14 tc-10 px-1">
                <div className="me-auto">ยอดรวม</div>
                <div id="txsum">{formatNumber(billdata?.total_price || 0)}</div>
              </div>
              <div className="d-flex fs-12 tc-10 px-1">
                <div className="me-auto">ส่วนลดท้ายบิล</div>
                <div>{formatNumber(billdata?.discount_amount || 0)}</div>
              </div>
              <div className="d-flex fs-12 tc-10 px-1">
                <div className="me-auto">มูลค่าสินค้าไม่รวมภาษี</div>
                <div id="txdiscount">
                  {formatNumber(billdata?.orderPrice?.taxable_amount || 0)}
                </div>
              </div>
              <div className="d-flex fs-12 tc-10 px-1">
                <div className="me-auto">ภาษี</div>
                <div id="txtax">
                  {formatNumber(billdata?.orderPrice?.vat || 0)}
                </div>
              </div>
            </div>
          </div>
        </Collapse>
        <button
          className={`btn collapse-total ${collapseShow ? "" : "collapsed"} `}
          type="button"
          onClick={() => setCollapseShow(!collapseShow)}
          aria-controls="collapse1"
          aria-expanded={collapseShow}
          style={{ borderRadius: "8px" }}
        >
          <div className="d-flex align-items-center">
            <div className="me-auto">ยอดรวมทั้งหมด</div>
            <div id="txnet">
              {formatNumber(
                billdata?.orderPrice?.final_profit_loss_rounded ||
                  billdata?.orderPrice?.sumprice ||
                  0
              )}
            </div>
          </div>
        </button>
      </div>
    </div>
  );

  return (
    <div>
      <div
        className="p-3 position-relative"
        style={{
          borderRadius: "8px",
          background: "#FFF",
          boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.14)",
        }}
      >
        {/* <Button className="btn btn-st-6 mb-3" onClick={() => navigate(-1)}>
          กลับสู่หน้ารายงาน
        </Button> */}
        {billdata ? (
          <div className="row gx-3">
            <div className="col-4">
              <div
                className="ps-3 py-2 d-flex flex-column"
                style={{
                  height: "calc(100vh - 175px)",
                  borderRadius: "8px",
                  background: "#FFF",
                  boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.14)",
                }}
              >
                <div
                  className="overflow-auto"
                  style={{ height: "calc(100vh - 235px)" }}
                >
                  <CustomerInfoSection />
                </div>
                <div className="mb-auto"></div>
              </div>
            </div>
            <div className="col-4">
              <BillDetailsSection />
            </div>

            <div className="col-4">
              <div
                className="pe-3"
                style={{ height: "calc(100vh - 235px)", overflow: "auto" }}
              >
                <ReceiptPrintPage
                  key={"print-receipt"}
                  ref={billRef}
                  order={billdata}
                  user=""
                  bill={orderBill}
                  pos={currentPOS}
                  type="receipt"
                  copy={true}
                />
              </div>
              <div className="pt-4 pb-2 d-flex justify-content-center">
                <button
                  className="btn btn-x1 text-[#FFF] bg-[#2192FF]"
                  disabled={billdata.billStatus === BILL_STATUS.CANCELED}
                  onClick={handlePrint}
                >
                  พิมพ์
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ height: "calc(100vh - 150px)" }}
          >
            <div className="text-center">
              <div className="mt-3 text-[#6D788D]">ไม่พบรายการ</div>
            </div>
          </div>
        )}
      </div>

      <ReportBillDetailPrintPopup
        isOpen={isPrintPopupOpen}
        
        onPrintComplete={handlePrintComplete}
        billData={billdata}
        orderBill={orderBill}
        currentPOS={currentPOS}
      />
    </div>
  );
}
